import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'

interface SearchOrCreateComboboxProps<T> {
    /** Offered while the search box is empty. */
    items: T[]
    currentId: string | null
    /** Shown when the current entry isn't in `items` (e.g. the order's expanded note). */
    currentLabel?: string
    getId: (item: T) => string
    getLabel: (item: T) => string
    /** Second line under the label (e.g. the delivery charge). */
    renderDetail?: (item: T) => ReactNode
    /** Server search, so entries beyond the first page can be found too. */
    search: (query: string) => Promise<T[]>
    onSelect: (item: T) => void
    /** Called with the trimmed query from the `Create "…"` row. */
    onCreate: (query: string) => void
    placeholder: string
    searchPlaceholder: string
    emptyText: string
    busy?: boolean
}

/**
 * Order cell picker: pick an entry or create one from what was typed. Same interaction as `CustomerSearch`: the query
 * is searched on the server (debounced), and the create row appears only once the results for that exact query are in
 * and none of them has the same name, so the picker doesn't create duplicates.
 */
export const SearchOrCreateCombobox = <T,>({
    items,
    currentId,
    currentLabel,
    getId,
    getLabel,
    renderDetail,
    search,
    onSelect,
    onCreate,
    placeholder,
    searchPlaceholder,
    emptyText,
    busy,
}: SearchOrCreateComboboxProps<T>) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<T[]>([])
    const [searchedQuery, setSearchedQuery] = useState('')
    const [searching, setSearching] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Callers pass a new `search` each render; the ref keeps the debounce keyed on the query alone.
    const searchRef = useRef(search)
    searchRef.current = search
    const trimmed = query.trim()

    useEffect(() => {
        if (!open || !trimmed) {
            setSearching(false)
            return
        }
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setSearching(true)
        debounceRef.current = setTimeout(async () => {
            try {
                setResults(await searchRef.current(trimmed))
            } catch {
                setResults([])
            } finally {
                setSearchedQuery(trimmed)
                setSearching(false)
            }
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [trimmed, open])

    const handleOpenChange = (next: boolean) => {
        setOpen(next)
        if (!next) {
            setQuery('')
            setResults([])
            setSearchedQuery('')
        }
    }

    const handleSelect = (item: T) => {
        handleOpenChange(false)
        onSelect(item)
    }

    const handleCreate = () => {
        handleOpenChange(false)
        onCreate(trimmed)
    }

    const shown = trimmed ? (searchedQuery === trimmed ? results : []) : items
    const resultsReady = !!trimmed && !searching && searchedQuery === trimmed
    const exactMatch = shown.some((item) => getLabel(item).trim().toLowerCase() === trimmed.toLowerCase())
    const current = items.find((item) => getId(item) === currentId)
    const label = (current && getLabel(current)) || (currentId && currentLabel) || placeholder

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-[200px] max-w-full justify-between font-normal"
                    disabled={busy}>
                    {busy ? <Spinner className="size-4" /> : <span className="truncate">{label}</span>}
                    <ChevronsUpDown className="ml-2 size-4 shrink-0 text-muted-foreground" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
                    <CommandList>
                        {trimmed && searching && (
                            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                                <Spinner className="size-4" />
                                Searching…
                            </div>
                        )}
                        {(resultsReady || !trimmed) && shown.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
                        {(resultsReady || !trimmed) && shown.length > 0 && (
                            <CommandGroup>
                                {shown.map((item) => (
                                    <CommandItem key={getId(item)} value={getId(item)} onSelect={() => handleSelect(item)}>
                                        <Check
                                            className={cn(
                                                'mr-2 size-4 shrink-0',
                                                currentId === getId(item) ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                        <div className="flex min-w-0 flex-col">
                                            <span className="truncate">{getLabel(item)}</span>
                                            {renderDetail && (
                                                <span className="text-xs text-muted-foreground">{renderDetail(item)}</span>
                                            )}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {resultsReady && !exactMatch && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem value={`create:${trimmed}`} onSelect={handleCreate}>
                                        <Plus className="mr-2 size-4 shrink-0" />
                                        <span className="truncate">Create “{trimmed}”</span>
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
